import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { ProTip } from '../../components/LessonUI';

const PythonOperatorsLogical = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Python Operators</div>
                <h1>Logical & Bitwise Operators</h1>
                <p className="hero-lead">
                    Master Boolean logic, bitwise manipulation, and membership testing - the operators that control
                    program flow and enable low-level data manipulation in Python.
                </p>
            </div>

            <div className="content-section chapter-intro">
                <p>
                    Logical operators (<code>and</code>, <code>or</code>, <code>not</code>) form the foundation of
                    conditional logic in Python, while bitwise operators provide direct access to binary data
                    manipulation. Understanding these operators is essential for writing efficient conditionals,
                    implementing algorithms, and working with low-level data where performance matters.
                </p>
                <p>
                    This chapter covers logical operators and short-circuit evaluation, identity operators
                    (<code>is</code> vs <code>==</code>), membership operators (<code>in</code>), and bitwise operations
                    for binary manipulation. You'll learn when to use each operator, how they combine to create
                    powerful expressions, and the advanced patterns that professional developers use in production code.
                </p>

                <div className="learning-objectives">
                    <h3>What You'll Learn</h3>
                    <ul>
                        <li>Logical operators (<code>and</code>, <code>or</code>, <code>not</code>) and short-circuit evaluation</li>
                        <li>Identity operators and the difference between <code>is</code> and <code>==</code></li>
                        <li>Membership operators for testing containment</li>
                        <li>Bitwise operators for binary data manipulation</li>
                        <li>Advanced patterns: guard clauses, feature flags, permission systems</li>
                        <li>Performance optimization and debugging techniques</li>
                    </ul>
                </div>
            </div>

            <div className="content-section">
                <h2>Logical Operators: and, or, not</h2>
                <p>
                    Python's logical operators work with boolean values and use short-circuit evaluation for efficiency.
                    Unlike many languages that use symbols (<code>&&</code>, <code>||</code>), Python uses readable
                    English keywords that make code self-documenting.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Logical Operators in Action</span>
                    </div>
                    <CodeBlock language="python" code={`# and - Returns True only if both operands are True
print(True and True)    # True
print(True and False)   # False
print(False and False)  # False

# or - Returns True if at least one operand is True
print(True or False)    # True
print(False or False)   # False
print(True or True)     # True

# not - Negates the boolean value
print(not True)   # False
print(not False)  # True

# Combining with comparisons
x = 10
print(x &gt; 5 and x < 15)     # True (x is between 5 and 15)
print(x < 5 or x &gt; 15)      # False (x is neither)
print(not (x < 5))          # True (x is not less than 5)

# Chaining comparisons (Pythonic!)
age = 25
print(18 <= age <= 65)      # True - clean and readable
print(age >= 18 and age <= 65)  # Same but more verbose`} />
                </div>

                <h3>The Concept of "Truthiness"</h3>
                <p>
                    In Python, every object can be evaluated as a boolean. This is different from strict languages like Java
                    where a condition <em>must</em> be a boolean. Python asks: "Is this object Truthy or Falsy?"
                </p>

                <div className="comparison-table">
                    <h4>Truth Value Reference Table</h4>
                    <table>
                        <thead>
                            <tr>
                                <th>Category</th>
                                <th>Falsy Values (Evaluates to False)</th>
                                <th>Truthy Values (Everything else)</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><strong>Constants</strong></td>
                                <td><code>False</code>, <code>None</code></td>
                                <td><code>True</code></td>
                            </tr>
                            <tr>
                                <td><strong>Numbers</strong></td>
                                <td><code>0</code>, <code>0.0</code>, <code>0j</code></td>
                                <td><code>1</code>, <code>-5</code>, <code>0.0001</code></td>
                            </tr>
                            <tr>
                                <td><strong>Sequences</strong></td>
                                <td><code>""</code> (Empty String), <code>[]</code>, <code>()</code></td>
                                <td><code>"a"</code>, <code>[0]</code>, <code>(None,)</code></td>
                            </tr>
                            <tr>
                                <td><strong>Collections</strong></td>
                                <td><code>{'{'}{'}'}</code> (Empty Dict), <code>set()</code></td>
                                <td><code>{'{'}'a': 1{'}'}</code>, <code>{'{'}0{'}'}</code></td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <ProTip><div className="info-icon"></div>
                    <div className="info-text">
                        <strong>Under the Hood:</strong> Python checks for a <code>__bool__</code> method.
                        If not found, it checks <code>__len__</code>. If <code>len()</code> is 0, it's False.
                        Otherwise, it's True.
                    </div></ProTip>

                <h3>Short-Circuit Evaluation</h3>
                <p>
                    Python's logical operators use "short-circuit" evaluation - they stop evaluating as soon as the
                    result is determined. For <code>and</code>, if the first operand is false, the second is never
                    evaluated. For <code>or</code>, if the first operand is true, the second is skipped. This isn't
                    just an optimization - it's a fundamental feature that prevents errors and enables elegant patterns.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Short-Circuit Evaluation</span>
                    </div>
                    <CodeBlock language="python" code={`# and stops at first False
result = False and expensive_function()  # expensive_function() never runs
print(result)  # False

# or stops at first True
result = True or expensive_function()    # expensive_function() never runs
print(result)  # True

# Practical use: preventing errors
x = 0
# Without short-circuit, this would crash:
# result = (1 / x) or "undefined"  # ZeroDivisionError!

# But this works:
result = x != 0 and (10 / x)  # False (division never happens)
print(result)  # False

# Safe dictionary access
user = {"name": "Alice"}
email = "email" in user and user["email"] != ""
print(email)  # False - 'email' key doesn't exist, so second part never runs

# Default values with or
name = ""
display_name = name or "Anonymous"  # Use "Anonymous" if name is empty
print(display_name)  # "Anonymous"`} />
                </div>

                <p>
                    The examples above demonstrate the critical safety features of short-circuit evaluation. When
                    dividing by a number that might be zero, checking <code>x != 0</code> first prevents the
                    ZeroDivisionError from ever occurring. Similarly, when accessing dictionary keys that might not
                    exist, using <code>in</code> before the access operation ensures you never get a KeyError.
                </p>

                <p>
                    The default value pattern with <code>or</code> is particularly elegant in Python. When you write
                    <code>display_name = name or "Anonymous"</code>, Python first evaluates <code>name</code>. If
                    it's empty (which is falsy), Python short-circuits and uses "Anonymous" instead. This pattern
                    works because Python's <code>or</code> operator returns the first truthy value, not just True or
                    False. This makes configuration defaults and fallback values incredibly clean and readable.
                </p>

                <p>
                    Understanding short-circuit evaluation is essential for writing defensive code that anticipates
                    edge cases. In production systems, you'll often need to handle missing data, null values, or
                    unexpected states. Short-circuit evaluation lets you write validation logic that's both safe and
                    performant, checking conditions in order from most likely to least likely, or from cheapest to
                    most expensive to evaluate.
                </p>

                <h3>Advanced Short-Circuit Patterns</h3>
                <p>
                    Short-circuit evaluation isn't just about preventing errors - it's a powerful tool for writing
                    cleaner, more efficient code. Understanding these patterns helps you write Pythonic code that
                    other developers immediately recognize and understand. These patterns are widely used in
                    professional codebases and understanding them will help you read and write production-quality
                    Python code.
                </p>

                <p>
                    Guard clauses are a defensive programming technique where you check for invalid conditions at
                    the start of a function and return early if those conditions are met. This approach keeps the
                    main logic of your function unindented and easier to read, rather than wrapping everything in
                    nested if statements. When you return early for invalid data, the rest of your function can
                    assume the data is valid, reducing the need for additional checks.
                </p>

                <p>
                    The "first truthy value" pattern is incredibly useful when you have multiple potential sources
                    for a value and want to use the first one that's actually set. This is common in configuration
                    systems where you might check environment variables, then config files, then hardcoded defaults.
                    Python's <code>or</code> operator makes this pattern elegant because it returns the actual value,
                    not just a boolean, allowing you to chain multiple fallbacks together naturally.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Guard Clauses and Default Values</span>
                    </div>
                    <CodeBlock language="python" code={`# Pattern 1: Guard clauses in functions
def process_data(data):
    # Quick exit if data is invalid - rest of function doesn't run
    if not data or not isinstance(data, list):
        return None
    
    # Safe to process data here
    return [item * 2 for item in data]

# Pattern 2: Default values with 'or'
config = {}
max_retries = config.get('retries') or 3  # Use 3 if not in config or 0
timeout = config.get('timeout') or 30

# Pattern 3: First truthy value
name = username or email or "Guest"  # Use first non-empty value

# Pattern 4: Lazy evaluation for expensive operations
def get_cached_or_fetch(cache_key):
    # Only call expensive fetch_from_db if cache miss
    return cache.get(cache_key) or fetch_from_db(cache_key)

# Pattern 5: Chained conditions for validation
def is_valid_email(email):
    return (
        email and                          # Not empty
        '@' in email and                   # Has @ symbol
        email.count('@') == 1 and          # Only one @
        len(email.split('@')[1]) &gt; 3       # Domain has length
    )  # All must be true, stops at first False`} />
                </div>

                <p>
                    These five patterns represent some of the most common uses of short-circuit evaluation in
                    professional Python code. The guard clause pattern (Pattern 1) is particularly important for
                    maintaining clean code architecture. Instead of nesting your entire function inside an if
                    statement, you check for invalid inputs first and return immediately. This keeps your main
                    logic at the top level of indentation, making it much easier to read and maintain.
                </p>

                <p>
                    Pattern 2's default value handling is ubiquitous in configuration systems. When a configuration
                    value might be missing or explicitly set to zero/empty, using <code>or</code> provides a clean
                    fallback. However, be careful with this pattern - if zero is a valid configuration value,
                    you'll need to use <code>if value is None</code> instead to distinguish between "not set" and
                    "set to zero."
                </p>

                <p>
                    The lazy evaluation pattern (Pattern 4) is crucial for performance optimization. Database queries,
                    file I/O, and API calls are expensive operations. By checking the cache first with
                    <code>or</code>, you only pay the cost of the expensive <code>fetch_from_db()</code> call when
                    absolutely necessary. This can dramatically improve application performance, especially for
                    frequently accessed data.
                </p>

                <p>
                    The email validation pattern (Pattern 5) demonstrates how chaining short-circuit conditions
                    creates natural validation flows. Each condition is checked in order, and as soon as one fails,
                    the entire expression returns False without checking the remaining conditions. This is both
                    efficient (avoiding unnecessary checks) and safe (later conditions can assume earlier ones
                    passed). For example, <code>email.count('@')</code> only runs if <code>'@' in email</code> was
                    True, so you never call methods on None.
                </p>

                <ProTip><div className="info-icon"></div>
                    <div className="info-text">
                        <strong>Performance Tip:</strong> Order conditions from cheapest to most expensive. Python
                        evaluates left-to-right, so put fast checks first to avoid running slow operations
                        unnecessarily.
                    </div></ProTip>
            </div>

            <div className="content-section">
                <h2>Boolean Algebra in Python</h2>
                <p>
                    Understanding boolean algebra helps you simplify complex conditions and write clearer code.
                    De Morgan's laws, in particular, are essential for refactoring nested conditionals into more
                    readable forms.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>De Morgan's Laws and Simplification</span>
                    </div>
                    <CodeBlock language="python" code={`# De Morgan's Law 1: not (A and B) == (not A) or (not B)
# Complex condition
if not (is_admin and has_permission):
    print("Access denied")

# Equivalent simpler form
if not is_admin or not has_permission:
    print("Access denied")

# De Morgan's Law 2: not (A or B) == (not A) and (not B)
# Complex
if not (age < 18 or age &gt; 65):
    print("Working age")

# Simpler (and more intuitive!)
if age >= 18 and age <= 65:
    print("Working age")

# Even better with chaining
if 18 <= age <= 65:
    print("Working age")

# Simplifying complex nested conditions
# Before: Hard to read
if not (not user.is_active or (user.is_banned and not user.has_appeal)):
    allow_access()

# After: Much clearer
if user.is_active and (not user.is_banned or user.has_appeal):
    allow_access()

# Truth table analysis example
def can_edit(is_owner, is_admin, is_moderator):
    # Owner OR (Admin OR Moderator)
    return is_owner or (is_admin or is_moderator)
    # Equivalent to: is_owner or is_admin or is_moderator
    
# Combining with parentheses for clarity
can_publish = (
    (is_author and content_approved) or
    (is_editor and not requires_review) or
    is_admin
)`} />
                </div>

                <p>
                    De Morgan's laws might seem like abstract mathematics, but they have immediate practical
                    applications in everyday Python programming. When you encounter complex conditions with multiple
                    <code>not</code> operators, applying De Morgan's laws can often simplify them dramatically. The
                    key insight is that negating a compound condition flips both the operator (<code>and</code>
                    becomes <code>or</code> and vice versa) and negates each individual condition.
                </p>

                <p>
                    The working age example demonstrates why this matters for readability. The original condition
                    <code>not (age &lt; 18 or age &gt; 65)</code> requires mental gymnastics to understand - you're
                    checking for NOT (young OR old), which means "not young AND not old," which means "in the middle."
                    But if you apply De Morgan's law and think about what you actually want (age between 18 and 65),
                    you get the much clearer <code>age &gt;= 18 and age &lt;= 65</code>. Python's comparison chaining
                    makes this even better: <code>18 &lt;= age &lt;= 65</code> reads exactly like English.
                </p>

                <p>
                    Complex nested conditions like the access control example are common in real applications. The
                    original version with double negatives (<code>not (not user.is_active or ...)</code>) is nearly
                    impossible to understand at a glance. After simplification using De Morgan's laws, the logic
                    becomes clear: grant access if the user is active AND either they're not banned OR they have an
                    appeal. Each transformation makes the code more maintainable because future developers (including
                    you, six months from now) can understand the logic without working through the boolean algebra.
                </p>

                <p>
                    When combining multiple conditions, use parentheses liberally even when they're not strictly
                    required by operator precedence. The <code>can_publish</code> example shows how breaking complex
                    conditions across multiple lines with clear grouping makes the logic self-documenting. Anyone
                    reading this code can immediately see the three paths to publishing: be an author with approval,
                    be an editor without review requirements, or be an admin.
                </p>
            </div>

            <div className="content-section">
                <h2>Identity Operators: is vs ==</h2>
                <p>
                    The <code>is</code> operator checks if two variables reference the exact same object in memory
                    (identity), while <code>==</code> checks if values are equal. This distinction is critical for
                    correct comparisons.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Identity vs Equality</span>
                    </div>
                    <CodeBlock language="python" code={`# Equality (==) - compares values
list1 = [1, 2, 3]
list2 = [1, 2, 3]
print(list1 == list2)  # True - same values
print(list1 is list2)  # False - different objects

# Identity (is) - compares object IDs
list3 = list1
print(list1 is list3)  # True - exact same object

# The None check - ALWAYS use 'is'
value = None
if value is None:  # Correct
    print("Value is None")

if value == None:  #  Works but not recommended
    print("This works but use 'is' instead")

# Integer caching surprise
a = 256
b = 256
print(a is b)  # True (Python caches small integers -5 to 256)

c = 1000
d = 1000
print(c is d)  # False (larger integers not cached)
print(c == d)  # True (but values are equal)

# Use == for value comparison
if c == d:  # Correct for value comparison
    print("Values are equal")

# Use 'is' for None, True, False (singletons)
if value is None:  # Correct
    pass
if flag is True:   # Correct (though 'if flag:' is better)
    pass`} />
                </div>

                <p>
                    The distinction between <code>is</code> and <code>==</code> trips up many Python developers,
                    but understanding it is crucial for writing correct code. The <code>is</code> operator checks
                    object identity - whether two variables point to the exact same object in memory. The
                    <code>==</code> operator checks value equality - whether two objects have the same content, even
                    if they're different objects.
                </p>

                <p>
                    The integer caching behavior (Python caches integers from -5 to 256) is an implementation detail
                    you shouldn't rely on. That's why <code>1000 is 1000</code> returns False - each 1000 is a
                    separate object. However, <code>256 is 256</code> returns True because Python reuses the same
                    object for frequently-used small integers to save memory. This optimization is invisible for
                    value comparison with <code>==</code>, which is why you should always use <code>==</code> for
                    comparing numbers.
                </p>

                <p>
                    For None, True, and False, you should ALWAYS use <code>is</code>. These are singletons in Python
                    - there's only one None object, one True object, and one False object in the entire Python
                    process. Checking <code>value is None</code> is not only more idiomatic Python, it's also
                    slightly faster than <code>value == None</code> because it's a simple memory address comparison
                    rather than a method call. More importantly, custom objects can override <code>==</code> to return
                    unexpected results, but they can't override <code>is</code>.
                </p>

                <p>
                    The subtle bug that can occur with incorrect use of <code>is</code> for value comparison is
                    particularly insidious because it might work in testing but fail in production. If you write
                    <code>if user_id is 1</code>, it might work during development (where small integers are cached),
                    but fail when user_id is 1000. Always use <code>==</code> for value comparison to avoid these
                    hard-to-debug issues.
                </p>
            </div>

            <div className="content-section">
                <h2>Membership Operators: in and not in</h2>
                <p>
                    Membership operators test whether a value exists in a sequence (string, list, tuple, set, dictionary).
                    These are highly optimized in Python and are the idiomatic way to test containment.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Membership Testing</span>
                    </div>
                    <CodeBlock language="python" code={`# Lists
fruits = ["apple", "banana", "cherry"]
print("apple" in fruits)     # True
print("grape" not in fruits)  # True

# Strings (substring search)
text = "Hello, World!"
print("World" in text)    # True
print("Python" in text)   # False
print("world" in text)    # False (case-sensitive!)

# Tuples
coordinates = (10, 20, 30)
print(20 in coordinates)  # True

# Sets (fastest membership testing!)
valid_codes = {"admin", "user", "guest"}
print("admin" in valid_codes)  # True - O(1) constant time!

# Dictionaries (tests keys by default)
user = {"name": "Alice", "age": 30}
print("name" in user)      # True
print("Alice" in user)     # False (checks keys, not values)
print("Alice" in user.values())  # True (explicit value check)

# Performance matters for large collections
#  SLOW for large lists
large_list = list(range(1000000))
print(999999 in large_list)  # O(n) - checks every element

# FAST with sets
large_set = set(range(1000000))
print(999999 in large_set)   # O(1) - instant lookup!`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Bitwise Operators</h2>
                <p>
                    Bitwise operators manipulate individual bits in integers. While less common in everyday programming,
                    they're essential for flags, permissions, low-level operations, and performance-critical code.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Bitwise Operations</span>
                    </div>
                    <CodeBlock language="python" code={`# Bitwise AND (&) - Both bits must be 1
print(12 & 10)  # 8  (1100 & 1010 = 1000)

# Bitwise OR (|) - At least one bit must be 1
print(12 | 10)  # 14 (1100 | 1010 = 1110)

# Bitwise XOR (^) - Bits must be different
print(12 ^ 10)  # 6  (1100 ^ 1010 = 0110)

# Bitwise NOT (~) - Flips all bits (returns -(n+1))
print(~12)  # -13

# Left shift (<<) - Multiply by 2^n
print(5 << 1)   # 10 (5 * 2^1)
print(5 << 2)   # 20 (5 * 2^2)

# Right shift (>>) - Divide by 2^n
print(20 >> 1)  # 10 (20 / 2^1)
print(20 >> 2)  # 5  (20 / 2^2)

# Common pattern: Flags and permissions
READ    = 1  # 0001
WRITE   = 2  # 0010
EXECUTE = 4  # 0100
DELETE  = 8  # 1000

# Combine permissions with OR
user_permissions = READ | WRITE  # 0011 (can read and write)

# Check permission with AND
can_read = bool(user_permissions & READ)    # True
can_delete = bool(user_permissions & DELETE)  # False

# Add permission
user_permissions |= EXECUTE  # Now 0111

# Remove permission
user_permissions &= ~WRITE   # Now 0101 (remove write)

print(f"Permissions: {bin(user_permissions)}")`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Performance Optimization with Logical Operators</h2>
                <p>
                    While Python's logical operators are already optimized, understanding their performance
                    characteristics helps you write faster code, especially in loops or when dealing with large
                    datasets.
                </p>

                <div className="comparison-table">
                    <h3>Performance Comparison</h3>
                    <table>
                        <thead>
                            <tr>
                                <th>Operation</th>
                                <th>Time Complexity</th>
                                <th>Best Use Case</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><code>x in list</code></td>
                                <td>O(n)</td>
                                <td>Small lists (&lt;100 items)</td>
                            </tr>
                            <tr>
                                <td><code>x in set</code></td>
                                <td>O(1)</td>
                                <td>Large collections, repeated lookups</td>
                            </tr>
                            <tr>
                                <td><code>x in dict</code></td>
                                <td>O(1)</td>
                                <td>Key-value associations</td>
                            </tr>
                            <tr>
                                <td><code>x in string</code></td>
                                <td>O(n*m)</td>
                                <td>Substring search</td>
                            </tr>
                            <tr>
                                <td>Bitwise operations</td>
                                <td>O(1)</td>
                                <td>Flags, permissions, binary math</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div className="code-example">
                    <div className="code-header">
                        <span>Optimization Examples</span>
                    </div>
                    <CodeBlock language="python" code={`#  SLOW for large lists
valid_users = ['user1', 'user2', ...thousand more...]
for request in requests:
    if request.user in valid_users:  # O(n) lookup each time!
        process(request)

# FAST - Convert to set once
valid_users_set = set(valid_users)  # One-time cost
for request in requests:
    if request.user in valid_users_set:  # O(1) lookup!
        process(request)

# Condition ordering matters
#  Expensive check first
if expensive_db_query() and simple_check():
    pass

# Cheap check first (short-circuits faster)
if simple_check() and expensive_db_query():
    pass

# Bitwise vs boolean for flags
import time

# Using booleans (more memory)
user_can_read = True
user_can_write = True  
user_can_delete = False

# Using bitwise flags (compact, faster)
READ = 1    # 0001
WRITE = 2   # 0010 
DELETE = 4  # 0100

user_permissions = READ | WRITE  # 0011
if user_permissions & READ:     # Instant check!
    allow_read()

# All checks in one integer vs 3+ boolean variables`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Functional Logic: any() and all()</h2>
                <p>
                    Writing loops to check if "at least one item is True" or "all items are True" is so common that
                    Python provides built-in functions for it. These functions are highly optimized and short-circuit
                    just like <code>or</code> and <code>and</code>.
                </p>

                <h3>1. any(iterable)</h3>
                <p>
                    Returns <code>True</code> if <em>at least one</em> element is Truthy. Equivalent to a chain of <code>or</code>.
                </p>

                <h3>2. all(iterable)</h3>
                <p>
                    Returns <code>True</code> only if <em>every</em> element is Truthy. Equivalent to a chain of <code>and</code>.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Cleaner Logic with any() / all()</span>
                    </div>
                    <CodeBlock language="python" code={`users = [
    {'{'}"name": "Alice", "active": True{'}'},
    {'{'}"name": "Bob", "active": False{'}'},
    {'{'}"name": "Charlie", "active": True{'}'}
]

# Check if anyone is active
has_active_users = any(u['active'] for u in users)
print(has_active_users)  # True

# Check if EVERYONE is active
all_active = all(u['active'] for u in users)
print(all_active)        # False

# Real-world Validation
password = "Password123"
requirements = [
    len(password) >= 8,
    any(char.isdigit() for char in password),
    any(char.isupper() for char in password)
]

if all(requirements):
    print("Strong Password! ")
else:
    print("Weak Password  weak ")`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Industry Application Patterns</h2>
                <p>
                    Real-world applications use logical operators in specific patterns. Understanding these patterns
                    helps you write professional-grade code that other developers recognize immediately.
                </p>

                <div className="content-section">
                    <h3>Pattern 1: Feature Flags (A/B Testing)</h3>
                    <p>
                        Feature flags allow you to enable or disable features without deploying new code.
                        This relies heavily on short-circuit evaluation. If the feature flag is missing,
                        the code should safely default to "False" without crashing.
                    </p>
                    <CodeBlock language="python" code={`class FeatureFlags:
    def __init__(self):
        self.flags = {
            'new_ui': {'enabled': True, 'rollout': 50}
        }
    
    def is_enabled(self, feature, user_id=0):
        # 1. Check if feature exists
        # 2. Check if globally enabled
        # 3. Check if user is in rollout bucket
        return (
            feature in self.flags and
            self.flags[feature]['enabled'] and
            hash(user_id) % 100 < self.flags[feature]['rollout']
        )`} />
                    <p>
                        This single return statement replaces what would be 3 nested <code>if</code> statements in other languages.
                        Python evaluates them left-to-right. If the feature isn't in <code>self.flags</code>, the rest never runs
                        (preventing a `KeyError`).
                    </p>
                </div>

                <div className="content-section">
                    <h3>Pattern 2: Bitwise Permission Systems</h3>
                    <p>
                        In high-performance systems (like Linux file permissions or database engines), storing 30+ boolean permissions
                        as separate columns is inefficient. Instead, we use a single integer and "Bitmasks".
                    </p>
                    <CodeBlock language="python" code={`# Define flags (powers of 2)
READ    = 1  # 0001
WRITE   = 2  # 0010
EXECUTE = 4  # 0100

class FileSystem:
    def __init__(self, perms):
        self.perms = perms

    def can_write(self):
        # Check if the WRITE bit is set
        return bool(self.perms & WRITE)

    def add_execute(self):
        # Set the EXECUTE bit
        self.perms |= EXECUTE

f = FileSystem(READ | WRITE)  # 3 (0011)
print(f.can_write())  # True`} />
                </div>

                <div className="content-section">
                    <h3>Pattern 3: Input Validation with Generators</h3>
                    <p>
                        Combining <code>all()</code> with generator expressions is the cleanest way to validate data.
                        It reads like a requirement checklist.
                    </p>
                    <CodeBlock language="python" code={`def validate_password(pwd):
    rules = [
        len(pwd) >= 8,                     # Length
        any(c.isupper() for c in pwd),     # Uppercase
        any(c.isdigit() for c in pwd),     # Digit
        "@" in pwd or "!" in pwd           # Special char
    ]
    
    if all(rules):
        return True, "Valid"
    else:
        # Find which rules failed (Advanced: requires checking individually)
        return False, "Password too weak"`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Debugging Complex Conditions</h2>
                <p>
                    Complex boolean expressions can be hard to debug. These strategies help you identify why
                    conditions aren't behaving as expected.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Debugging Techniques</span>
                    </div>
                    <CodeBlock language="python" code={`# Technique 1: Break down complex conditions
#  Hard to debug
if user.is_active and not user.is_banned and (user.is_premium or user.trial_days &gt; 0) and user.email_verified:
    grant_access()

# Easier to debug - see which condition fails
is_active_user = user.is_active and not user.is_banned
has_subscription = user.is_premium or user.trial_days &gt; 0
is_verified = user.email_verified

print(f"Active: {is_active_user}, Subscription: {has_subscription}, Verified: {is_verified}")

if is_active_user and has_subscription and is_verified:
    grant_access()
else:
    print(f"Access denied. Checks: active={is_active_user}, sub={has_subscription}, verified={is_verified}")

# Technique 2: Use assertions for debugging
def process_order(order):
    # These help catch logic errors during development
    assert order is not None, "Order cannot be None"
    assert order.items, "Order must have items"
    assert order.total &gt; 0, f"Invalid total: {order.total}"
    
    # Process order
    ...

# Technique 3: Logging complex evaluations
import logging
import { ProTip } from '../../components/LessonUI';

def should_send_notification(user, event):
    result = (
        user.notifications_enabled and
        event.priority >= user.min_priority and
        event.category in user.subscribed_categories
    )
    
    if not result:
        logging.debug(
            f"Notification suppressed: enabled={user.notifications_enabled}, "
            f"priority={event.priority}>={user.min_priority}, "
            f"category '{event.category}' in {user.subscribed_categories}"
        )
    
    return result`} />
                </div>
            </div>

            <div className="content-section common-pitfalls">
                <h2>Common Pitfalls & How to Avoid Them</h2>

                <div className="pitfall-card">
                    <h3> Mistake 1: Using 'is' for Value Comparison</h3>
                    <CodeBlock language="python" code={`#  WRONG
a = 1000
b = 1000
if a is b:  # False! Different objects
    print("Same")

# CORRECT
if a == b:  # True - same values
    print("Equal values")

# Only use 'is' for None, True, False
if value is None:  # Correct
    pass`} />
                </div>

                <div className="pitfall-card">
                    <h3> Mistake 2: Confusing & with 'and'</h3>
                    <CodeBlock language="python" code={`#  WRONG - Bitwise AND, not logical AND
if x &gt; 5 & x < 10:  # Syntax error or wrong result!
    print("Between 5 and 10")

# CORRECT - Logical AND
if x &gt; 5 and x < 10:
    print("Between 5 and 10")

# & is for bitwise operations on integers
flags = (READ | WRITE) & permissions  # Correct use`} />
                </div>

                <div className="pitfall-card">
                    <h3> Mistake 3: Not Using Short-Circuit Evaluation</h3>
                    <CodeBlock language="python" code={`#  RISKY - Can crash
items = []
# if items[0] == "target":  # IndexError if list is empty!
#     process()

# CORRECT - Short-circuit prevents error
if items and items[0] == "target":
    process()  # Safe - items[0] only checked if list not empty`} />
                </div>
            </div>

            <div className="content-section best-practices">
                <h2>Best Practices</h2>

                <div className="practice-grid">
                    <div className="practice-card">
                        <h3>Do:</h3>
                        <ul>
                            <li>Use <code>is</code> for comparing to <code>None</code></li>
                            <li>Use <code>in</code> for membership testing</li>
                            <li>Leverage short-circuit evaluation for safety</li>
                            <li>Use sets for fast membership on large data</li>
                            <li>Use bitwise for flags and permissions</li>
                            <li>Chain comparisons: <code>a &lt; b &lt; c</code></li>
                            <li>Order conditions cheapest-first</li>
                            <li>Break down complex conditions for debugging</li>
                        </ul>
                    </div>

                    <div className="practice-card">
                        <h3> Don't:</h3>
                        <ul>
                            <li>Don't use <code>is</code> for value comparison</li>
                            <li>Don't confuse <code>&</code> with <code>and</code></li>
                            <li>Don't test <code>== True</code> or <code>== False</code></li>
                            <li>Don't forget operator precedence with mixed logic</li>
                            <li>Don't use <code>^</code> for exponentiation (it's XOR!)</li>
                            <li>Don't skip parentheses in complex conditions</li>
                            <li>Don't use lists for large membership tests</li>
                        </ul>
                    </div>
                </div>
            </div>

            <div className="content-section key-takeaways">
                <h2> Key Takeaways</h2>
                <div className="takeaway-grid">
                    <div className="takeaway-item">
                        <h3>1. Logical Operators</h3>
                        <p>
                            <code>and</code>, <code>or</code>, <code>not</code> use English keywords and short-circuit
                            evaluation for efficiency and safety.
                        </p>
                    </div>

                    <div className="takeaway-item">
                        <h3>2. is vs ==</h3>
                        <p>
                            <code>is</code> checks identity (same object), <code>==</code> checks equality (same value).
                            Always use <code>is</code> for <code>None</code>.
                        </p>
                    </div>

                    <div className="takeaway-item">
                        <h3>3. Membership Testing</h3>
                        <p>
                            <code>in</code> and <code>not in</code> are optimized for testing containment. Use sets for
                            O(1) membership checks.
                        </p>
                    </div>

                    <div className="takeaway-item">
                        <h3>4. Bitwise Operators</h3>
                        <p>
                            <code>&</code>, <code>|</code>, <code>^</code>, <code>~</code>, <code>&lt;&lt;</code>,
                            <code>&gt;&gt;</code> manipulate binary data directly - essential for flags and permissions.
                        </p>
                    </div>

                    <div className="takeaway-item">
                        <h3>5. Short-Circuit Evaluation</h3>
                        <p>
                            Logical operators stop evaluating as soon as the result is known, preventing errors and
                            improving performance.
                        </p>
                    </div>

                    <div className="takeaway-item">
                        <h3>6. Performance Matters</h3>
                        <p>
                            Use sets instead of lists for large membership tests. Order conditions from cheapest to most
                            expensive. Bitwise operations are faster than multiple boolean checks.
                        </p>
                    </div>
                </div>
            </div>

        </div>
    );
};

export default PythonOperatorsLogical;


